/**
 * LogViewerPage - Real-time log viewing dashboard
 * 
 * Full-page layout for viewing and filtering EdgeLex logs.
 * Protected route (admin/developer only).
 * 
 * Phase 2: Added AI Assistant panel for log analysis.
 * 
 * @see /documentation/Observability/EdgeLex_Observability_Logger_System.md
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Wifi, WifiOff, RefreshCw, Download, FileJson, Activity, Layers, ChevronsUpDown, Sparkles, X, Server, Cloud, Zap } from 'lucide-react';
import { useLogStream, LogStreamFilters } from './hooks/useLogStream';
import { LogFilters, LogFiltersState } from './components/LogFilters';
import { LogTable } from './components/LogTable';
import { LogDetailModal } from './components/LogDetailModal';
import { ComponentRegistryModal } from './components/ComponentRegistryModal';
import { LogAIChatPanel, LogAIChatPanelHandle } from './components/LogAIChatPanel';
import { LogStorageManager } from './components/LogStorageManager';
import { exportToCSV, exportToJSON } from './utils/exportLogs';
import type { LogEvent } from '../../utils/WeBelieveLogger.types';

const DEFAULT_FILTERS: LogFiltersState = {
  level: [],
  component: '',
  search: '',
  sessionId: '',
  startTime: new Date(Date.now() - 60 * 60 * 1000).toISOString(), // Last 1 hour
  endTime: new Date().toISOString(),
  timePreset: '1h',
};

export function LogViewerPage() {
  // Filter state
  const [filters, setFilters] = useState<LogFiltersState>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<LogFiltersState>(DEFAULT_FILTERS);
  
  // UI state
  const [autoScroll, setAutoScroll] = useState(true);
  const [selectedLog, setSelectedLog] = useState<LogEvent | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [isQueryLoading, setIsQueryLoading] = useState(false);
  const [historicalLogs, setHistoricalLogs] = useState<LogEvent[]>([]);
  const [components, setComponents] = useState<string[]>([]);
  const [isComponentRegistryOpen, setIsComponentRegistryOpen] = useState(false);
  const [expandAll, setExpandAll] = useState(false);
  const [isAIPanelOpen, setIsAIPanelOpen] = useState(false);
  const [highlightedLogIds, setHighlightedLogIds] = useState<Set<string>>(new Set());
  const [forceModel, setForceModel] = useState<'auto' | 'local' | 'cloud'>('cloud');

  // Ref for AI Chat Panel to call explainLog programmatically
  const aiChatPanelRef = useRef<LogAIChatPanelHandle>(null);

  // Get realm ID from auth context (fallback to localStorage)
  const realmId = useMemo(() => {
    try {
      const token = localStorage.getItem('webelieve_access_token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.realm_id || payload.tenantId || 'default';
      }
    } catch {
      // Ignore parsing errors
    }
    return 'default';
  }, []);

  // Convert filter state to stream filters
  const streamFilters: LogStreamFilters = useMemo(() => ({
    level: appliedFilters.level.length > 0 ? appliedFilters.level : undefined,
    component: appliedFilters.component || undefined,
    sessionId: appliedFilters.sessionId || undefined,
    search: appliedFilters.search || undefined,
  }), [appliedFilters]);

  // Real-time log stream
  const {
    logs: streamLogs,
    isConnected,
    isReconnecting,
    logsPerSecond,
    connectionError,
    pause,
    resume,
    clear,
    isPaused,
  } = useLogStream(streamFilters);

  // Combine historical and streaming logs
  const allLogs = useMemo(() => {
    const combined = [...streamLogs, ...historicalLogs];
    // Deduplicate by id if present, otherwise by timestamp + message
    const seen = new Set<string>();
    return combined.filter(log => {
      const key = (log as any).id?.toString() || `${log.timestamp}-${log.message}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [streamLogs, historicalLogs]);

  // Real-time level counts from logs in view
  const levelCounts = useMemo(() => {
    const counts = { debug: 0, info: 0, warn: 0, error: 0, performance: 0 };
    for (const log of allLogs) {
      if (log.level in counts) {
        counts[log.level as keyof typeof counts]++;
      }
    }
    return counts;
  }, [allLogs]);

  // Fetch components for filter dropdown
  useEffect(() => {
    const fetchComponents = async () => {
      try {
        const token = localStorage.getItem('webelieve_access_token');
        const response = await fetch('/api/logs/components', {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setComponents(data.components || []);
        }
      } catch (error) {
        console.error('[LogViewer] Failed to fetch components:', error);
      }
    };
    fetchComponents();
  }, []);

  // Query historical logs
  const queryLogs = useCallback(async () => {
    setIsQueryLoading(true);
    try {
      const token = localStorage.getItem('webelieve_access_token');
      const params = new URLSearchParams();
      
      if (appliedFilters.level.length > 0) {
        params.set('level', appliedFilters.level.join(','));
      }
      if (appliedFilters.component) {
        params.set('component', appliedFilters.component);
      }
      if (appliedFilters.sessionId) {
        params.set('session_id', appliedFilters.sessionId);
      }
      if (appliedFilters.search) {
        params.set('search', appliedFilters.search);
      }
      if (appliedFilters.startTime) {
        params.set('startTime', appliedFilters.startTime);
      }
      if (appliedFilters.endTime) {
        params.set('endTime', appliedFilters.endTime);
      }
      params.set('limit', '500');

      const response = await fetch(`/api/logs/query?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setHistoricalLogs(data.logs || []);
      } else {
        console.error('[LogViewer] Query failed:', response.status);
      }
    } catch (error) {
      console.error('[LogViewer] Query error:', error);
    } finally {
      setIsQueryLoading(false);
    }
  }, [appliedFilters]);

  // Apply filters - sync filters to appliedFilters and trigger query
  // Recalculate time values based on preset to ensure fresh time range
  const handleApplyFilters = useCallback(() => {
    const TIME_PRESETS: Record<string, number> = {
      '10s': 10 * 1000,
      '60s': 60 * 1000,
      '5m': 5 * 60 * 1000,
      '1h': 60 * 60 * 1000,
      '24h': 24 * 60 * 60 * 1000,
      '7d': 7 * 24 * 60 * 60 * 1000,
    };
    
    let updatedFilters = { ...filters };
    
    // Recalculate time range based on preset (unless custom)
    if (filters.timePreset !== 'custom' && TIME_PRESETS[filters.timePreset]) {
      const ms = TIME_PRESETS[filters.timePreset];
      updatedFilters.endTime = new Date().toISOString();
      updatedFilters.startTime = new Date(Date.now() - ms).toISOString();
    }
    
    console.log('[LogViewer] Applying filters:', {
      level: updatedFilters.level,
      component: updatedFilters.component,
      timePreset: updatedFilters.timePreset,
      startTime: updatedFilters.startTime,
      endTime: updatedFilters.endTime,
    });
    
    setFilters(updatedFilters);
    setAppliedFilters(updatedFilters);
  }, [filters]);

  // Auto-query when appliedFilters change (real-time search)
  useEffect(() => {
    console.log('[LogViewer] Applied filters changed, querying with:', {
      level: appliedFilters.level,
      component: appliedFilters.component,
      startTime: appliedFilters.startTime,
      endTime: appliedFilters.endTime,
      timePreset: appliedFilters.timePreset,
    });
    queryLogs();
  }, [appliedFilters, queryLogs]);

  // Auto-apply ALL filter changes with debounce (not just search)
  const handleApplyRef = useRef(handleApplyFilters);
  handleApplyRef.current = handleApplyFilters;
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip initial render — the appliedFilters→queryLogs effect handles that
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      handleApplyRef.current();
    }, 100);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.component,
    filters.search,
    filters.sessionId,
    filters.timePreset,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    filters.level.join(','),
  ]);

  // Clear filters
  const handleClearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setHistoricalLogs([]);
    clear();
  }, [clear]);

  // Row click handler
  const handleRowClick = useCallback((log: LogEvent, index: number) => {
    setSelectedLog(log);
    setSelectedIndex(index);
  }, []);

  // Modal navigation
  const handlePrevLog = useCallback(() => {
    if (selectedIndex > 0) {
      const newIndex = selectedIndex - 1;
      setSelectedIndex(newIndex);
      setSelectedLog(allLogs[newIndex]);
    }
  }, [selectedIndex, allLogs]);

  const handleNextLog = useCallback(() => {
    if (selectedIndex < allLogs.length - 1) {
      const newIndex = selectedIndex + 1;
      setSelectedIndex(newIndex);
      setSelectedLog(allLogs[newIndex]);
    }
  }, [selectedIndex, allLogs]);

  // Export handlers
  const handleExportCSV = useCallback(() => {
    exportToCSV(allLogs);
  }, [allLogs]);

  const handleExportJSON = useCallback(() => {
    exportToJSON(allLogs);
  }, [allLogs]);

  // AI Panel highlight handler
  const handleHighlightLogs = useCallback((logIds: string[]) => {
    setHighlightedLogIds(new Set(logIds));
    // Clear highlights after 10 seconds
    setTimeout(() => {
      setHighlightedLogIds(new Set());
    }, 10000);
  }, []);

  // Handle Explain Log button click from LogTable
  const handleExplainLog = useCallback((focusLog: LogEvent, contextLogs: LogEvent[]) => {
    // Open AI panel if not already open
    setIsAIPanelOpen(true);

    // Wait for panel to render, then call explainLog
    setTimeout(() => {
      aiChatPanelRef.current?.explainLog(focusLog, contextLogs);
    }, 100);

    // Highlight the focus log
    const logKey = `${focusLog.timestamp}-${focusLog.session_id}-${focusLog.component}`;
    setHighlightedLogIds(new Set([logKey]));
  }, []);

  // Component registry selection handler
  const handleSelectComponent = useCallback((component: string) => {
    const newFilters = { ...filters, component };
    setFilters(newFilters);
    setAppliedFilters(newFilters);
    // Trigger query with new filter
    setIsQueryLoading(true);
    const token = localStorage.getItem('webelieve_access_token');
    const params = new URLSearchParams();
    params.set('component', component);
    if (newFilters.startTime) params.set('startTime', newFilters.startTime);
    if (newFilters.endTime) params.set('endTime', newFilters.endTime);
    params.set('limit', '500');
    
    fetch(`/api/logs/query?${params.toString()}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => setHistoricalLogs(data.logs || []))
      .catch(err => console.error('[LogViewer] Query error:', err))
      .finally(() => setIsQueryLoading(false));
  }, [filters]);

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-3">
        {/* Top row: Title, connection, stats, and action buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            {/* Title and stats column */}
            <div className="flex flex-col">
              <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                EdgeLex Logs Viewer
              </h1>
              <div className="flex items-center gap-4 mt-1">
                {/* Connection Status */}
                {isConnected ? (
                  <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                    <Wifi className="w-3.5 h-3.5" />
                    <span className="text-xs font-medium">Connected</span>
                  </div>
                ) : isReconnecting ? (
                  <div className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400">
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span className="text-xs font-medium">Reconnecting...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
                    <WifiOff className="w-3.5 h-3.5" />
                    <span className="text-xs font-medium">Disconnected</span>
                  </div>
                )}
                {/* Logs per second */}
                <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                  <Activity className="w-3.5 h-3.5" />
                  <span className="text-xs font-mono">{logsPerSecond} logs/sec</span>
                </div>
                {/* Total logs in view */}
                <div className="flex items-center gap-1 text-gray-600 dark:text-gray-300">
                  <span className="text-xs font-medium">{allLogs.length.toLocaleString()} logs in view</span>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Storage Manager */}
            <LogStorageManager
              onDeleteComplete={() => {
                // Refresh logs and clear stale stream data after deletion
                queryLogs();
                clear();
              }}
            />

            {/* AI Assistant Button */}
            <button
              onClick={() => setIsAIPanelOpen(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
              title="Open AI Log Assistant"
            >
              <Sparkles className="w-4 h-4" />
              AI Assistant
            </button>

            <button
              onClick={() => setIsComponentRegistryOpen(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-lex-accent text-lex-accent-contrast rounded-md hover:bg-lex-accent-hover transition-colors"
            >
              <Layers className="w-4 h-4" />
              Components
            </button>

            <button
              onClick={() => setExpandAll(!expandAll)}
              className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-md transition-colors ${
                expandAll
                  ? 'bg-lex-accent text-lex-accent-contrast hover:bg-lex-accent-hover'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
              title={expandAll ? 'Collapse all rows' : 'Expand all rows to show details'}
            >
              <ChevronsUpDown className="w-4 h-4" />
              {expandAll ? 'Collapse' : 'Expand All'}
            </button>

            <button
              onClick={isPaused ? resume : pause}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                isPaused
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              {isPaused ? 'Resume' : 'Pause'}
            </button>
            
            <button
              onClick={handleExportCSV}
              disabled={allLogs.length === 0}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Download className="w-4 h-4" />
              CSV
            </button>
            
            <button
              onClick={handleExportJSON}
              disabled={allLogs.length === 0}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FileJson className="w-4 h-4" />
              JSON
            </button>
          </div>
        </div>

        {/* Connection error */}
        {connectionError && (
          <div className="mt-2 text-sm text-red-600 dark:text-red-400">
            Connection error: {connectionError}
          </div>
        )}
      </header>

      {/* Filters */}
      <LogFilters
        filters={filters}
        onFiltersChange={setFilters}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        components={components}
        isLoading={isQueryLoading}
        levelCounts={levelCounts}
      />

      {/* Log Table */}
      <LogTable
        logs={allLogs}
        onRowClick={handleRowClick}
        autoScroll={autoScroll}
        onAutoScrollChange={setAutoScroll}
        isLoading={isQueryLoading}
        searchTerm={appliedFilters.search}
        expandAll={expandAll}
        onExpandAllChange={setExpandAll}
        highlightedLogIds={highlightedLogIds}
        onExplainLog={handleExplainLog}
      />

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-3">
        <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
          <span>
            Showing {allLogs.length.toLocaleString()} logs
            {streamLogs.length > 0 && ` (${streamLogs.length} streaming)`}
          </span>
          <span>
            Auto-scroll: {autoScroll ? 'On' : 'Off'}
          </span>
        </div>
      </footer>

      {/* Detail Modal */}
      <LogDetailModal
        log={selectedLog}
        onClose={() => setSelectedLog(null)}
        onPrev={handlePrevLog}
        onNext={handleNextLog}
        hasPrev={selectedIndex > 0}
        hasNext={selectedIndex < allLogs.length - 1}
        onFilterRelated={(sessionId, operation) => {
          const newFilters = {
            ...filters,
            sessionId: sessionId || '',
            search: operation || '',
          };
          setFilters(newFilters);
          setAppliedFilters(newFilters);
        }}
      />

      {/* Component Registry Modal */}
      <ComponentRegistryModal
        isOpen={isComponentRegistryOpen}
        onClose={() => setIsComponentRegistryOpen(false)}
        onSelectComponent={handleSelectComponent}
      />

      {/* AI Assistant Right Panel */}
      {isAIPanelOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-25 z-40 transition-opacity duration-300"
            onClick={() => setIsAIPanelOpen(false)}
          />

          {/* Panel */}
          <div
            className={`
              fixed right-0 top-0 h-full w-[500px] max-w-full bg-white dark:bg-gray-800
              shadow-2xl border-l border-gray-200 dark:border-gray-700 z-50
              transform transition-transform duration-300 ease-in-out flex flex-col
              ${isAIPanelOpen ? 'translate-x-0' : 'translate-x-full'}
            `}
          >
            {/* Panel Header */}
            <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-600 to-purple-700 text-white">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5" />
                <div>
                  <h3 className="font-semibold">Log Intelligence Assistant</h3>
                  <p className="text-xs text-purple-100">AI-powered log analysis</p>
                </div>
              </div>
              
              {/* Model Toggle */}
              <div className="flex items-center gap-1 bg-white bg-opacity-20 rounded-lg p-0.5">
                <button
                  onClick={() => setForceModel('auto')}
                  className={`flex items-center gap-1 px-2 py-1 text-xs rounded-md transition-colors ${
                    forceModel === 'auto' 
                      ? 'bg-white text-purple-700 font-medium' 
                      : 'text-white hover:bg-white hover:bg-opacity-10'
                  }`}
                  title="Auto-select based on query complexity"
                >
                  <Zap className="w-3 h-3" />
                  Auto
                </button>
                <button
                  onClick={() => setForceModel('local')}
                  className={`flex items-center gap-1 px-2 py-1 text-xs rounded-md transition-colors ${
                    forceModel === 'local' 
                      ? 'bg-white text-purple-700 font-medium' 
                      : 'text-white hover:bg-white hover:bg-opacity-10'
                  }`}
                  title="Force local model (Qwen 1.5B - faster)"
                >
                  <Server className="w-3 h-3" />
                  Local
                </button>
                <button
                  onClick={() => setForceModel('cloud')}
                  className={`flex items-center gap-1 px-2 py-1 text-xs rounded-md transition-colors ${
                    forceModel === 'cloud' 
                      ? 'bg-white text-purple-700 font-medium' 
                      : 'text-white hover:bg-white hover:bg-opacity-10'
                  }`}
                  title="Force cloud model (GPT-4 - smarter)"
                >
                  <Cloud className="w-3 h-3" />
                  Cloud
                </button>
              </div>

              <button
                onClick={() => setIsAIPanelOpen(false)}
                className="p-1.5 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Panel Content */}
            <LogAIChatPanel
              ref={aiChatPanelRef}
              realmId={realmId}
              onHighlightLogs={handleHighlightLogs}
              forceModel={forceModel}
              className="flex-1 min-h-0"
            />
          </div>
        </>
      )}
    </div>
  );
}

export default LogViewerPage;
